/**
 * Browser event handlers for canvas interactions
 * Extracted from MyCanvas.js to reduce file size
 */

/**
 * Creates zoom change handler
 * @param {Function} detectBrowserZoom - Function to detect browser zoom level
 * @returns {Function} Handler function for zoom changes
 */
export const createZoomChangeHandler = (detectBrowserZoom) => {
  return () => {
    detectBrowserZoom();
  };
};

/**
 * Creates resize handler
 * @param {Function} detectBrowserZoom - Function to detect browser zoom level
 * @returns {Function} Handler function for resize events
 */
export const createResizeHandler = (detectBrowserZoom) => {
  return () => {
    detectBrowserZoom();
  };
};

/**
 * Creates touch start handler for pinch-to-zoom
 * @param {Function} setInitialDistance - Function to set initial distance
 * @param {Function} setInitialZoom - Function to set initial zoom
 * @param {number} zoomLevel - Current zoom level
 * @returns {Function} Handler function for touch start events
 */
export const createTouchStartHandler = (
  setInitialDistance,
  setInitialZoom,
  zoomLevel
) => {
  return (e) => {
    // Ignore touch events from webcam popup
    const target = e.target;
    if (
      target &&
      (target.closest(".webcam-overlay") ||
        target.closest(".webcam-content") ||
        target.closest(".live-camera"))
    ) {
      return;
    }

    if (e.touches.length === 2) {
      // Calculate initial distance between two fingers
      const touch1 = e.touches[0];
      const touch2 = e.touches[1];
      const distance = Math.sqrt(
        Math.pow(touch2.clientX - touch1.clientX, 2) +
          Math.pow(touch2.clientY - touch1.clientY, 2)
      );
      setInitialDistance(distance);
      setInitialZoom(zoomLevel);
    }
  };
};

/**
 * Creates touch move handler for pinch-to-zoom
 * @param {number} initialDistance - Initial distance between fingers
 * @param {number} initialZoom - Initial zoom level
 * @param {Function} setZoomLevel - Function to set zoom level
 * @param {Function} setZoomInputValue - Function to set zoom input value
 * @returns {Function} Handler function for touch move events
 */
export const createTouchMoveHandler = (
  initialDistance,
  initialZoom,
  setZoomLevel,
  setZoomInputValue
) => {
  return (e) => {
    // Ignore touch events from webcam popup
    const target = e.target;
    if (
      target &&
      (target.closest(".webcam-overlay") ||
        target.closest(".webcam-content") ||
        target.closest(".live-camera"))
    ) {
      return;
    }

    if (e.touches.length === 2) {
      e.preventDefault(); // Prevent default pinch behavior

      // Calculate current distance between two fingers
      const touch1 = e.touches[0];
      const touch2 = e.touches[1];
      const currentDistance = Math.sqrt(
        Math.pow(touch2.clientX - touch1.clientX, 2) +
          Math.pow(touch2.clientY - touch1.clientY, 2)
      );

      if (initialDistance > 0) {
        // Calculate zoom factor based on distance change
        const zoomFactor = currentDistance / initialDistance;
        const newZoom = initialZoom * zoomFactor;

        // Clamp zoom level
        const clampedZoom = Math.max(0.1, Math.min(newZoom, 5));

        // Apply zoom
        const container = document.getElementById("app-container");
        if (container) {
          container.style.transform = `scale(${clampedZoom})`;
          // Use top center origin to prevent top content cutoff
          container.style.transformOrigin = "top center";
          setZoomLevel(clampedZoom);
          setZoomInputValue(Math.round(clampedZoom * 100));
        }
      }
    }
  };
};

/**
 * Sets up browser zoom detection event listeners
 * @param {Function} detectBrowserZoom - Function to detect browser zoom level
 * @returns {Function} Cleanup function to remove event listeners
 */
export const setupBrowserZoomListeners = (detectBrowserZoom) => {
  const handleZoomChange = createZoomChangeHandler(detectBrowserZoom);
  const handleResize = createResizeHandler(detectBrowserZoom);

  // Listen for visual viewport changes (modern browsers)
  if (window.visualViewport) {
    window.visualViewport.addEventListener("resize", handleZoomChange);
    window.visualViewport.addEventListener("scroll", handleZoomChange);
  }

  // Listen for window resize (fallback)
  window.addEventListener("resize", handleResize);

  // Listen for orientation change (mobile)
  window.addEventListener("orientationchange", handleZoomChange);

  // Return cleanup function
  return () => {
    if (window.visualViewport) {
      window.visualViewport.removeEventListener("resize", handleZoomChange);
      window.visualViewport.removeEventListener("scroll", handleZoomChange);
    }
    window.removeEventListener("resize", handleResize);
    window.removeEventListener("orientationchange", handleZoomChange);
  };
};
